'use client';

import { ChangeEvent, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { CookieName, getClientCookieValue } from '@headless-workspace/core-domain';
import {
    GlowButton,
    GlowClickable,
    GlowIcon,
    GlowTextField,
    GlowTitle,
    Icons,
    PropsWithClassName,
} from '@headless-workspace/glow-ds';
import { CUSTOMER_GROUP_SEPARATOR, DateHelpers, ShopTheFutureHelpers } from '@headless-workspace/utils';
import { deleteShopTheFutureContextServerAction, setShopTheFutureContextServerAction } from '../../../server';
import { CustomerGroupList } from './CustomerGroupList';

type ShopTheFutureProps = PropsWithClassName & {
    onPreviewClose: () => void;
    onPreviewHide: () => void;
    customerGroups?: string[];
};

export const ShopTheFuture = ({
    className,
    onPreviewClose,
    onPreviewHide,
    customerGroups = [],
}: ShopTheFutureProps): ReactNode => {
    const t = useTranslations('Preview.shopInTheFuture');

    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [effectiveDate, setEffectiveDate] = useState<Date>(
        DateHelpers.toDate(
            ShopTheFutureHelpers.getEffectiveDate(getClientCookieValue(CookieName.ShopTheFutureContext)),
        ) ?? new Date(),
    );
    const [selectedCustomerGroups, setSelectedCustomerGroups] = useState<string[]>(
        ShopTheFutureHelpers.getCustomerGroups(getClientCookieValue(CookieName.ShopTheFutureContext))?.split(
            CUSTOMER_GROUP_SEPARATOR,
        ) ?? [],
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    const onChangeInputValue = (inputType: 'date' | 'time') => (event: ChangeEvent<HTMLInputElement>) => {
        switch (inputType) {
            case 'date':
                setDate(event.target.value);
                break;
            case 'time':
                setTime(event.target.value);
                break;
        }
    };

    const onValidate = useCallback(async () => {
        setIsSubmitting(true);

        const newDate = new Date(DateHelpers.toISODate(date, time));

        setEffectiveDate(newDate);
        await setShopTheFutureContextServerAction(newDate, selectedCustomerGroups);

        setIsSubmitting(false);
        onPreviewClose();
    }, [date, onPreviewClose, selectedCustomerGroups, time]);

    const setDateAndTime = useCallback((date: Date) => {
        setDate(date.toISOString().slice(0, 10));
        setTime(date.toTimeString().slice(0, 5));
    }, []);

    const onClose = useCallback(() => {
        onPreviewClose();
    }, [onPreviewClose]);

    const onReset = useCallback(async () => {
        setIsResetting(true);
        const newDate = new Date();

        await deleteShopTheFutureContextServerAction();

        setIsResetting(false);
        setEffectiveDate(newDate);
        setDateAndTime(newDate);

        onPreviewClose();
    }, [setDateAndTime, onPreviewClose, setEffectiveDate]);

    useEffect(() => {
        setDateAndTime(effectiveDate);
    }, [setDateAndTime, effectiveDate]);

    const orderedCustomerGroups = useMemo(() => customerGroups.sort((a, b) => a.localeCompare(b)), [customerGroups]);

    return (
        <div
            className={clsx(
                'flex flex-col gap-1',
                'bg-background-l1 p-1.5',
                'border-medium border-solid rounded-0.5 border-border-primary',
                className,
            )}
        >
            <div className={'flex flex-row gap-0.5 justify-between'}>
                <GlowTitle fontSize={'title3'} text={t('label.title')} />
                <GlowClickable
                    className={'fill-text-primary-fixed hover:fill-text-secondary active:fill-text-secondary-inverse'}
                    content={{ icon: <GlowIcon Icon={Icons.Cross} type={'large'} /> }}
                    onClick={onClose}
                />
            </div>

            <div className={'flex flex-col gap-0.5'}>
                <GlowTitle fontSize={'title4'} text={t('label.dateAndTime')} />

                <div className={'flex gap-0.5'}>
                    <GlowTextField
                        className={'p-0.5 rounded-0.5 border-medium border-background-inverse'}
                        type={'date'}
                        value={date}
                        onChange={onChangeInputValue('date')}
                    />
                    <GlowTextField
                        className={'p-0.5 rounded-0.5 border-medium border-background-inverse'}
                        type={'time'}
                        value={time}
                        onChange={onChangeInputValue('time')}
                    />
                </div>
            </div>
            <CustomerGroupList
                orderedCustomerGroups={orderedCustomerGroups}
                selectedCustomerGroups={selectedCustomerGroups}
                setSelectedCustomerGroups={setSelectedCustomerGroups}
            />
            <div className={'flex items-center gap-1'}>
                <GlowButton
                    className={'w-full'}
                    label={t('action.ok')}
                    onClick={onValidate}
                    isSpinnerActive={isSubmitting}
                />
                <GlowButton
                    className={'w-full'}
                    label={t('action.reset')}
                    variant={'tertiary'}
                    onClick={onReset}
                    isSpinnerActive={isResetting}
                />
            </div>
            <GlowButton label={t('action.hide')} variant={'secondary'} onClick={onPreviewHide} />
        </div>
    );
};
